import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, signal, ViewChild, inject } from '@angular/core';
import {Icon, inputErrors, User, UserSandbox, utils} from '../../../modules/global';
import {FormControl} from '@angular/forms';
import {filter, takeUntil} from 'rxjs/operators';
import {Observable, Subject} from 'rxjs';
import {ChatBotSandbox} from '../../../modules/global/sandboxes/chat-bot.sandbox';
import {marked} from 'marked';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { NgIf, AsyncPipe } from '@angular/common';
import { ButtonIconComponent } from '../../../modules/shared/components/buttons/button-icon/button-icon.component';
import { SvgIconComponent } from '../../../modules/shared/components/svg-icon/svg-icon.component';
import { MonogramComponent } from '../../../modules/shared/components/monogram/monogram.component';
import { TextInputComponent } from '../../../modules/shared/components/inputs/text-input/text-input.component';
import { MatBadge } from '@angular/material/badge';

@Component({
    selector: 'slm-floating-chat',
    templateUrl: './floating-chat.component.html',
    styleUrls: ['./floating-chat.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, ButtonIconComponent, SvgIconComponent, MonogramComponent, TextInputComponent, MatBadge, AsyncPipe, TranslatePipe]
})
export class FloatingChatComponent implements OnInit, OnDestroy {
  private readonly userSandbox = inject(UserSandbox);
  private readonly chatbotSandbox = inject(ChatBotSandbox);
  private readonly translate = inject(TranslateService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  public readonly errors = inputErrors;
  public readonly icons = Icon;
  public showChatButton = signal<boolean>(true);
  public isLoading = signal<boolean>(false);
  public showChatButtonBadge = signal<boolean>(false)
  public chatMessageControl = new FormControl<string>('');

  public isChatOpen = false;
  public messages: Array<{message: string; type: 'user' | 'bot', time: string}> = [];

  public user$: Observable<User>;

  private destroy$ = new Subject();

  @ViewChild('chatMessageContainer') chatMessageContainer: ElementRef;

  constructor() {
    this.messages.push({message: this.translate.instant('CHATBOT_FIRST_QUESTION'), type: 'bot', time: utils.currentTime()});
  }

  ngOnInit() {
    this.user$ = this.userSandbox.user$.pipe(filter((user) => !!user));
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  openChat() {
    this.showChatButton.set(false);
    this.isChatOpen = true;
    this.changeDetector.detectChanges();
    this.scrollToBottom();
    this.showChatButtonBadge.set(false);
  }

  closeChat() {
    this.isChatOpen = false;
    this.showChatButton.set(true);
  }

  sendMessage() {
    const message = this.chatMessageControl.value;
    if (message.trim() && !this.isLoading()) {
      this.messages.push({message, type: 'user', time: utils.currentTime()});
      this.scrollToBottom();
      this.chatMessageControl.setValue('');
      this.isLoading.set(true);

      this.chatbotSandbox.sendMessage(message).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (value) => {
          const message = value?.answer || this.translate.instant('CHATBOT_ANSWER_ERROR');
          const markedMessage = marked.parse(message).toString().replace(/【\d+:\d+†\w+】/g, '');
          this.messages.push({message: markedMessage, type: 'bot', time: utils.currentTime()});
          this.isLoading.set(false);

          if(!this.isChatOpen){
            this.showChatButtonBadge.set(true);
          }

          this.scrollToBottom();
        },
        error: () => {
          this.messages.push({message: this.translate.instant('CHATBOT_ANSWER_ERROR'), type: 'bot', time: utils.currentTime()});
          this.isLoading.set(false);
          this.scrollToBottom();
        }
      });
    }
  }

  private scrollToBottom = () => {
    this.changeDetector.detectChanges();
    this.chatMessageContainer.nativeElement.scrollTop = this.chatMessageContainer.nativeElement?.scrollHeight;
  }
}
